import React, { useEffect, useState, useCallback } from "react";
import styled from "styled-components";
import CustomerTable from "./CustomerTable";
import { IconButton, Typography, TextField, InputAdornment } from "@material-ui/core";
import { CloudDownloadOutlined, RefreshRounded, Search } from "@material-ui/icons";
import api from "../../../services/api";
import CircularLoader from "../../UI/Loader";
import { COLORS } from "../../../assets/colors";
import { Button } from "@material-ui/core";
import { Add } from "@material-ui/icons";
import CreateUserModal from "./CreateUserModal";
import { useSelector } from "react-redux";

function Customers() {
  const { currentUser } = useSelector((state) => state.auth);
  const [customers, setCustomers] = useState(null);
  const [customersCount, setCustomersCount] = useState(0);
  const [loading, setLoading] = useState(false);
  const [pages, setPages] = useState(0);
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [isAddModalOpen, setIsAddModalOpen] = useState(false);
  const pageSize = 15;
  const limit = pageSize;
  const offset = (page - 1) * pageSize;

  const fetchUsers = async () => {
    try {
      setLoading(true);
      let response = await api.get("admin/user/", {
        params: { limit, offset, ...(searchQuery ? { q: searchQuery } : {}) },
      });

      if (response.status === 200) {
        setLoading(false);
        setCustomers(prev => page === 1 ? response.data.data : [...(prev || []), ...response.data.data]);
        setCustomersCount(response.data.count);
      }
    } catch (error) {
      setLoading(false);
    }
  };

  useEffect(() => {
    const timer = setTimeout(() => {
      if (page === 1) {
        fetchUsers();
      } else {
        setPage(1);
      }
    }, 500);

    return () => clearTimeout(timer);
  }, [searchQuery]);

  useEffect(() => {
    fetchUsers();
  }, [page]);

  useEffect(() => {
    setPages(Math.ceil(customersCount / pageSize));
  }, [customersCount]);

  const handleChange = (event, value) => {
    setPage(value);
  };

  return (
    <PageWrapper>
      <PageHeader>
        <div className="header-text">
          <h1>Customer Management</h1>
          <p>View and manage all registered users of GoSawari.</p>
        </div>
        <HeaderActions>
          <SearchWrapper>
            <TextField
              placeholder="Search by name, email or phone..."
              variant="outlined"
              size="small"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Search style={{ color: COLORS.adminTextSecondary }} />
                  </InputAdornment>
                ),
              }}
            />
          </SearchWrapper>
          <IconButton onClick={() => fetchUsers()}>
            <RefreshRounded />
          </IconButton>
          {currentUser?.email === "support@gosawari.com" && (
            <Button
              variant="contained"
              color="primary"
              startIcon={<Add />}
              onClick={() => setIsAddModalOpen(true)}
              style={{ borderRadius: "8px", textTransform: "none" }}
            >
              Add User
            </Button>
          )}
          <IconButton className="download-btn">
            <CloudDownloadOutlined />
          </IconButton>
        </HeaderActions>
      </PageHeader>

      <CreateUserModal
        open={isAddModalOpen}
        handleClose={() => setIsAddModalOpen(false)}
        onRefresh={fetchUsers}
      />

      <TableCard>
        {loading ? (
          <CircularLoader isLoading={loading} />
        ) : (
          <CustomerTable customers={customers} />
        )}
      </TableCard>

      <PaginationWrapper>
        {customers?.length < customersCount && (
          <Button
            variant="outlined"
            color="primary"
            onClick={() => setPage(prev => prev + 1)}
            disabled={loading}
            style={{ borderRadius: "8px", textTransform: "none", padding: "8px 24px" }}
          >
            {loading ? "Loading..." : "Load More"}
          </Button>
        )}
      </PaginationWrapper>
    </PageWrapper>
  );
}

export default Customers;

const PageWrapper = styled.div`
  display: flex;
  flex-direction: column;
  gap: 2rem;
`;

const PageHeader = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;

  .header-text {
    h1 {
      margin: 0;
      font-size: 1.875rem;
      font-weight: 700;
      color: ${COLORS.adminTextPrimary};
    }
    p {
      margin: 0.5rem 0 0 0;
      color: ${COLORS.adminTextSecondary};
      font-size: 1rem;
    }
  }
`;

const HeaderActions = styled.div`
  display: flex;
  gap: 1rem;

  .download-btn {
    background: ${COLORS.adminAccent};
    color: white;
    &:hover {
      background: ${COLORS.adminAccent};
      opacity: 0.9;
    }
  }
`;

const TableCard = styled.div`
  background: ${COLORS.adminCardBg};
  border-radius: 12px;
  box-shadow: ${COLORS.adminShadow};
  border: 1px solid rgba(0, 0, 0, 0.05);
  overflow: hidden;
`;

const SearchWrapper = styled.div`
  margin-right: 1.5rem;
  .MuiOutlinedInput-root {
    background: white;
    border-radius: 8px;
    width: 350px;
    transition: width 0.3s ease;
    
    &:hover .MuiOutlinedInput-notchedOutline {
      border-color: ${COLORS.adminAccent};
    }
    
    &.Mui-focused {
      width: 450px;
      .MuiOutlinedInput-notchedOutline {
        border-color: ${COLORS.adminAccent};
        border-width: 2px;
      }
    }
  }
`;

const PaginationWrapper = styled.div`
  display: flex;
  justify-content: center;
  padding: 1rem 0 3rem 0;
`;
